import { useEffect, useMemo, useState } from "react";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useBreadcrumb } from "../contexts/BreadcrumbContext.jsx";
import { useUser } from "../hooks/user.js";
import { backendGet } from "../services/dataService.js";
import { Card } from "../theme/components/index.js";
import { colorsSemantic } from "../theme/tokens/colors-semantic.js";
import useDashboardTaskFilters from "../hooks/useDashboardTaskFilters.js";
import TrendCard from "../components/dashboard/TrendCard.jsx";
import TaskListCard from "../components/dashboard/TaskListCard.jsx";
import WaitingSplitCard from "../components/dashboard/WaitingSplitCard.jsx";
import AgeBucketCard from "../components/dashboard/AgeBucketCard.jsx";
import SparteDonut from "../components/dashboard/SparteDonut.jsx";
import SubstitutionsCompactCard from "../components/dashboard/SubstitutionsCompactCard.jsx";

/**
 * The Home dashboard — a triage board matching the Schmidt click dummy: three
 * 12-month trend cards, the prioritised "Meine Aufgaben" queue, and a right-hand
 * column breaking the open workload down by responsibility, age and Sparte.
 *
 * Everything comes from one request. Tab / age / search filtering is client-side
 * over the loaded rows, so it is instant; the *counts* beside each filter come
 * from server aggregates over all open claims, so they keep describing the whole
 * workload rather than shrinking as you filter.
 */
export function HomePage() {
  const { setBreadcrumbs } = useBreadcrumb();
  const { user } = useUser();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    setBreadcrumbs([{ name: "Home", path: "/home" }]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    const controller = new AbortController();

    backendGet("/data/dashboard/stats", {
      label: "Dashboard",
      enqueueSnackbar,
      signal: controller.signal,
      setResult: (res) => setStats(res?.data ?? res),
    });

    return () => controller.abort();
  }, [enqueueSnackbar]);

  const trends = stats?.trends ?? [];
  const tasksBlock = stats?.tasks;
  const items = useMemo(() => tasksBlock?.items ?? [], [tasksBlock]);

  const filters = useDashboardTaskFilters(items);

  const firstName = user?.vorname || user?.full_name?.split(" ")[0] || "";
  const waitingRows = tasksBlock?.waiting ?? [];
  const ageBuckets = tasksBlock?.age_buckets ?? [];
  const openTotal = tasksBlock?.meta?.open_total ?? 0;

  const openTask = (task) =>
    navigate(
      // A brand-new claim still needs completing, so drop the broker straight
      // into the claim form rather than the overview.
      task.is_new ? `/schaeden/${task.id}?tab=schadenformular` : `/schaeden/${task.id}`
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {/* Greeting + board search */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "16px", mb: "18px", flexWrap: "wrap" }}>
        <Box sx={{ flex: "2 1 520px", minWidth: 0 }}>
          <Typography sx={{ fontSize: "20px", fontWeight: 700, letterSpacing: "-.3px" }}>
            Willkommen zurück{firstName ? `, ${firstName}` : ""}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: colorsSemantic.text.muted, mt: "3px" }}>
            Ihre offenen Vorgänge in der Reihenfolge, in der sie Aufmerksamkeit brauchen.
          </Typography>
        </Box>
        <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
          {/* Filters the loaded board in place — deliberately not ClaimsSearchBar,
              which queries the API per keystroke and navigates away. */}
          <TextField
            fullWidth
            size="small"
            value={filters.query}
            onChange={(event) => filters.setQuery(event.target.value)}
            placeholder="Suche in offenen Vorgängen …"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon sx={{ fontSize: 17, color: colorsSemantic.icon.muted }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      </Box>

      {/* Three 12-month trend cards */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "14px", mb: "16px" }}>
        {trends.map((trend) => (
          <Box key={trend.key} sx={{ flex: "1 1 240px", minWidth: 0 }}>
            <TrendCard trend={trend} />
          </Box>
        ))}
      </Box>

      {/* Board + right-hand breakdown */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-start" }}>
        <Box sx={{ flex: "2 1 520px", minWidth: 0 }}>
          <TaskListCard
            tasks={items}
            filtered={filters.filtered}
            counts={tasksBlock?.tabs}
            ageBuckets={ageBuckets}
            meta={tasksBlock?.meta}
            filters={filters}
            onOpenTask={openTask}
            onShowAll={() => navigate("/schaeden")}
          />
        </Box>

        <Box
          sx={{
            flex: "1 1 260px",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          <WaitingSplitCard
            rows={waitingRows}
            total={openTotal}
            onSelect={filters.selectWaiting}
          />

          <AgeBucketCard buckets={ageBuckets} selected={filters.age} onToggle={filters.toggleAge} />

          <Card variant="surface" sx={{ padding: "15px 17px" }}>
            <Typography
              sx={{
                fontSize: "13.5px",
                fontWeight: 700,
                color: colorsSemantic.text.strong,
                letterSpacing: "-.1px",
              }}
            >
              Schäden nach Sparte
            </Typography>
            {/* Says "im Bestand" on purpose: unlike the cards above it, this one
                counts closed claims too. */}
            <Typography sx={{ fontSize: "11.5px", color: colorsSemantic.text.muted, mt: "3px", mb: "14px" }}>
              Verteilung im Bestand
            </Typography>
            <SparteDonut
              total={stats?.sparte?.total ?? 0}
              segments={stats?.sparte?.segments ?? []}
              size={96}
            />
          </Card>

          <SubstitutionsCompactCard
            items={stats?.substitutions?.items ?? []}
            activeCount={stats?.substitutions?.active_count ?? 0}
          />
        </Box>
      </Box>
    </Box>
  );
}
